"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useModalScrollLock = void 0;
const react_1 = require("react");
/**
 * 自定義 hook 用於在 Modal 打開時管理滾動行為
 * - 禁用背景滾動
 * - 當滑鼠在 Modal 外面時，讓滾輪事件轉發到 Modal 內容
 *
 * @param isOpen - Modal 是否打開
 *
 * @example
 * ```tsx
 * const MyModal = ({ isOpen, onClose }) => {
 *   useModalScrollLock(isOpen);
 *
 *   if (!isOpen) return null;
 *
 *   return (
 *     <div className="fixed inset-0">
 *       <div className="overflow-y-auto">
 *         ...modal content...
 *       </div>
 *     </div>
 *   );
 * };
 * ```
 */
const useModalScrollLock = (isOpen) => {
    const modalRef = (0, react_1.useRef)(null);
    (0, react_1.useEffect)(() => {
        if (isOpen) {
            // 記錄當前滾動位置
            const scrollY = window.scrollY;
            // 禁用背景滾動
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.left = '0';
            document.body.style.right = '0';
            document.body.style.overflow = 'hidden';
            // 找到 Modal 內容區域（通常是第一個可滾動的元素）
            const findModalContent = () => {
                const modals = document.querySelectorAll('[role="dialog"], .modal, .fixed.inset-0');
                for (let i = 0; i < modals.length; i++) {
                    const modal = modals[i];
                    const scrollable = modal.querySelector('.overflow-y-auto, .overflow-auto, .scrollable');
                    if (scrollable) {
                        modalRef.current = scrollable;
                        return;
                    }
                    // 如果沒找到特定的可滾動元素，使用 modal 本身
                    if (modal.scrollHeight > modal.clientHeight) {
                        modalRef.current = modal;
                        return;
                    }
                }
            };
            // 處理滾輪事件，智能處理滾動行為
            const handleWheel = (e) => {
                const target = e.target;
                // 檢查滑鼠是否在可滾動元素內
                const isInsideScrollable = (element) => {
                    if (!element || element === document.body)
                        return false;
                    const styles = getComputedStyle(element);
                    const hasScroll = styles.overflowY === 'auto' ||
                        styles.overflowY === 'scroll' ||
                        styles.overflow === 'auto' ||
                        styles.overflow === 'scroll';
                    // 檢查是否真的可以滾動（內容高度大於容器高度）
                    const canScroll = element.scrollHeight > element.clientHeight;
                    if (hasScroll && canScroll) {
                        return true;
                    }
                    // 遞迴檢查父元素
                    return isInsideScrollable(element.parentElement);
                };
                // 如果滑鼠在可滾動元素內，讓原生滾動行為生效
                if (isInsideScrollable(target)) {
                    return; // 不阻止預設行為，讓原生滾動生效
                }
                // 否則阻止預設行為並轉發到主要 Modal 內容
                e.preventDefault();
                if (!modalRef.current) {
                    findModalContent();
                }
                if (modalRef.current) {
                    modalRef.current.scrollTop += e.deltaY;
                }
            };
            // 延遲查找 Modal 內容，確保 DOM 已渲染
            setTimeout(findModalContent, 100);
            // 監聽滾輪事件
            document.addEventListener('wheel', handleWheel, { passive: false });
            return () => {
                // 恢復背景滾動
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.left = '';
                document.body.style.right = '';
                document.body.style.overflow = '';
                // 恢復滾動位置
                window.scrollTo(0, scrollY);
                // 移除滾輪事件監聯器
                document.removeEventListener('wheel', handleWheel);
                modalRef.current = null;
            };
        }
    }, [isOpen]);
};
exports.useModalScrollLock = useModalScrollLock;
exports.default = exports.useModalScrollLock;
